"use client";

import React, { useEffect, useState } from 'react';
import { X, ShieldAlert, AlertTriangle, AlertCircle, Info, XCircle } from 'lucide-react';
import { useThreatToast, ThreatToast, ToastSeverity } from '@/contexts/ThreatToastContext';
import Icon from '@/components/ui/AppIcon';


// ─── Severity Config ──────────────────────────────────────────────────────────

const severityConfig: Record<ToastSeverity, {
  label: string;
  icon: React.ElementType;
  border: string;
  bg: string;
  badge: string;
  iconColor: string;
  glow: string;
}> = {
  critical: {
    label: 'CRITICAL',
    icon: XCircle,
    border: 'border-red-500/60',
    bg: 'bg-red-950/80',
    badge: 'bg-red-500 text-white',
    iconColor: 'text-red-400',
    glow: 'shadow-[0_0_16px_rgba(255,59,59,0.35)]',
  },
  high: {
    label: 'HIGH',
    icon: ShieldAlert,
    border: 'border-orange-500/60',
    bg: 'bg-orange-950/80',
    badge: 'bg-orange-500 text-white',
    iconColor: 'text-orange-400',
    glow: 'shadow-[0_0_12px_rgba(255,107,53,0.30)]',
  },
  medium: {
    label: 'MEDIUM',
    icon: AlertTriangle,
    border: 'border-yellow-500/50',
    bg: 'bg-yellow-950/70',
    badge: 'bg-yellow-500 text-black',
    iconColor: 'text-yellow-400',
    glow: 'shadow-[0_0_10px_rgba(255,184,0,0.20)]',
  },
  low: {
    label: 'LOW',
    icon: AlertCircle,
    border: 'border-blue-500/40',
    bg: 'bg-blue-950/70',
    badge: 'bg-blue-500 text-white',
    iconColor: 'text-blue-400',
    glow: '',
  },
  info: {
    label: 'INFO',
    icon: Info,
    border: 'border-cyber-border',
    bg: 'bg-[#0F1117]/90',
    badge: 'bg-cyber-muted text-white',
    iconColor: 'text-cyber-muted',
    glow: '',
  },
};

// ─── Single Toast ─────────────────────────────────────────────────────────────

function ToastItem({ toast }: { toast: ThreatToast }) {
  const { dismissToast } = useThreatToast();
  const [visible, setVisible] = useState(false);
  const cfg = severityConfig[toast.severity] || severityConfig.info;
  const Icon = cfg.icon;

  useEffect(() => {
    // Trigger entrance animation
    const t = setTimeout(() => setVisible(true), 20);
    return () => clearTimeout(t);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => dismissToast(toast.id), 300);
  };

  return (
    <div
      className={`
        relative flex items-start gap-3 rounded-xl border backdrop-blur-md px-4 py-3 w-[360px] max-w-[calc(100vw-2rem)]
        transition-all duration-300 ease-out
        ${cfg.border} ${cfg.bg} ${cfg.glow}
        ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}
      `}
    >
      {/* Severity stripe */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${cfg.badge.split(' ')[0]}`} />

      {/* Icon */}
      <div className={`shrink-0 mt-0.5 ${cfg.iconColor}`}>
        <Icon size={16} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded ${cfg.badge}`}>
            {cfg.label}
          </span>
          <span className="text-[11px] font-semibold text-cyber-text truncate">{toast.threat_type}</span>
        </div>
        <p className="text-[11px] text-cyber-muted font-mono truncate" title={toast.target}>
          Target: <span className="text-cyber-text">{toast.target}</span>
        </p>
        <p className="text-[10px] text-cyber-subtle font-mono mt-0.5">
          {toast.source_ip} · {toast.technique}
        </p>
      </div>

      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        className="shrink-0 w-5 h-5 flex items-center justify-center rounded-md hover:bg-white/10 transition-colors text-cyber-muted hover:text-cyber-text"
        aria-label="Dismiss alert"
      >
        <X size={12} />
      </button>
    </div>
  );
}

// ─── Toast Container ──────────────────────────────────────────────────────────

export default function ThreatToastContainer() {
  const { toasts, dismissAll } = useThreatToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[9999] flex flex-col gap-2.5 items-end pointer-events-none">
      {/* Dismiss all button */}
      {toasts.length > 1 && (
        <button
          onClick={dismissAll}
          className="pointer-events-auto text-[10px] font-mono text-cyber-muted hover:text-cyber-text bg-[#0F1117]/80 border border-cyber-border rounded-lg px-3 py-1.5 transition-colors backdrop-blur-sm"
        >
          Dismiss all ({toasts.length})
        </button>
      )}
      {/* Toast stack */}
      <div className="flex flex-col gap-2 pointer-events-auto">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} />
        ))}
      </div>
    </div>
  );
}
